/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import CssBaseline from '@material-ui/core/CssBaseline'; // TODO: Remove material-ui
import { extractAndSaveToDataLayer } from 'helpers';

import { SpinnerScreen, ModalScreen, SnackBarLoggedScreen as SnackBarScreen } from '@screen';
import { routes } from '@navigation/routes';
import { breakpoint, breakpoints } from '@constants/styles';
import { useAuth, useWidth } from '@hooks';
import {
  Icon,
  Divider,
  NavMenu,
  NavMenuItem,
  ModalToFsc,
  MenuFantasyNameTopBar,
  DropdownHeaderMenu,
  CalendarRankDays,
} from '@components';
import ModalContainer from '@containers/ModalContainer';
import { getEnv } from '@config';
import { Modal, theme } from 'fpay-ui';
import SideBar from './SideBar';

const { version } = getEnv();

const Wrapper = styled.div`
  display: flex;
  height: 100vh;
`;

const ImageFpay = styled.img`
  width: 85px;
`;

const RowTitles = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 32px;
  margin-bottom: 5px;
`;

const IconContainer = styled.div`
  display: flex;
  cursor: pointer;
  margin-right: 16px;
`;

const ContainerImageFpay = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  @media (min-width: 600px) {
    justify-content: flex-start;
  }
`;

const ContainerButton = styled.div`
  display: flex;
  width: 24px;
  margin-right: 24px;
  ${breakpoint('large')`
      display: none !important;
    `}
`;

const Content = styled.div`
  flex: 1;
  transition: width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms;
  overflow: hidden;
`;

const Body = styled.div`
  width: 100%;
  padding: 0px;
  height: calc(100vh - 64px);
  overflow-y: scroll;
  text-overflow: ellipsis;
  padding: ${({ mobileFullSize }) => (mobileFullSize ? `0px` : `0px 16px 24px 24px`)};
  ${breakpoint('medium')`
    height: calc(100vh - 56px);
    padding: 0px 16px 24px 24px;
  `}
  ${breakpoint('large')`
    height: calc(100vh - 118px);
    padding: 0px 16px 24px 24px;
  `}
  ::-webkit-scrollbar {
    width: 10px;
    border-radius: 0 10px 0 0;
  }
  ::-webkit-scrollbar-track {
    width: 10px;
    border-radius: 0 10px 0 0;
  }
  ::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 5px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: #555;
    border-radius: 5px;
  }
`;

const Title = styled.h2`
  ${breakpoint('large')`
    font-size: 24px;
    line-height: 32px;
  `}
  font-size: 18px;
  line-height: 21px;
  font-weight: 700;
  color: #505050;
  margin: 0px;
  flex: 1;
`;

const Description = styled.div`
  ${breakpoint('large')`
  font-size: 16px;
  margin-bottom: 32px
  `}
  display: flex;
  flex-direction: column;
  justify-items: flex-start;
  color: #505050;
  font-size: 14px;
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  position: sticky;
  z-index: 2;
  top: 0;
  height: 70px;
  background: #fff;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #ebebeb;
  padding: 0px 24px;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    z-index: 2;
    padding: 0px 24px;
  }
  ${breakpoint('large')`
    padding: 0px 24px !important;
  `}
  > svg {
    margin-right: 20px;
    display: block;
    width: 24px;
    ${breakpoint('large')`
      display: none !important;
    `}
  }
`;

const DividerStyled = styled(Divider)`
  width: 85%;
  height: 0;
  border: solid 1px #e4e6eb1f;
  border-width: thin;
  margin: 10px auto;
`;

const AppVersion = styled.div`
  ${({ theme }) => theme.textTypes['Overline-w400']}
  color: ${theme.colors.bbrand200};
  padding: 25px 0px 8px 24px;
`;

const NavLinkStyled = styled.a`
  text-decoration: none;
`;
const MainSideBar = ({ children, route }) => {
  const loguedAs = useSelector((state) => state.login.loguedAs);

  const { layoutProps } = route;
  const { t } = useTranslation();
  const { isOnus, isSeller, isAllowed } = useAuth();

  const history = useHistory();
  const width = useWidth();
  const merchant = useSelector((state) => state.login.merchant);

  const [isOpen, setIsOpen] = useState(window.innerWidth > breakpoints.large);
  const [showModal, setShowModal] = useState(false);
  const toggleShowModal = () => {
    setShowModal(!showModal);
  };

  useEffect(() => {
    extractAndSaveToDataLayer(loguedAs);
  }, []);

  const addRating =
    isSeller() && isAllowed('OPSCORE') && route.path.endsWith('settlement-calendar');

  return (
    <>
      <CssBaseline />
      <SpinnerScreen />
      <ModalScreen />
      <SnackBarScreen existNavBar />
      <ModalContainer />
      <Wrapper>
        <SideBar isOpen={isOpen} setIsOpen={setIsOpen} merchant={merchant}>
          <NavMenu routeSelected={route} routes={routes} navExpanded={isOpen} />
          <DividerStyled />
          {!isOnus && (
            <>
              <NavLinkStyled onClick={() => setShowModal(true)} id="sidebar_gsc">
                <NavMenuItem icon="global" navExpanded={isOpen} title="Falabella Seller Center" />
              </NavLinkStyled>
              <DividerStyled />
            </>
          )}
          <>
            <AppVersion>
              {t('common.appVersion')} {version}
            </AppVersion>
          </>
        </SideBar>
        <Content>
          <Header>
            <ContainerButton>
              <Icon icon="burger" size="24" color="#838aa2" onClick={() => setIsOpen(!isOpen)} />
            </ContainerButton>
            {width >= breakpoints.large && <MenuFantasyNameTopBar />}
            {width < breakpoints.large && (
              <ContainerImageFpay>
                <ImageFpay src="fpay-color.png" alt="" />
              </ContainerImageFpay>
            )}
            <DropdownHeaderMenu />
          </Header>
          <Body {...layoutProps}>
            <RowTitles>
              {route.parent && !route.parent.notBreadcrumb && (
                <IconContainer>
                  <Icon
                    icon="arrow-left"
                    size="24"
                    color="#838aa2"
                    onClick={() => history.push(route.parent.path)}
                  />
                </IconContainer>
              )}
              <Title>
                {t(
                  width <= breakpoints.medium && route.titleMobile ? route.titleMobile : route.title
                )}
              </Title>
            </RowTitles>
            <Description>
              <>
                {t(route.description)}
                <br />
                {addRating && <CalendarRankDays />}
              </>
            </Description>
            {children}
          </Body>
        </Content>
      </Wrapper>
      {showModal && (
        <Modal fClose={toggleShowModal} showCloseButton={false} width="344px">
          <ModalToFsc
            text={t('homeScreen.cards.text')}
            handleConfirm={() =>
              window.open(t('homeScreen.modalToFsc.linkToSC'), '_blank', 'noopener')
            }
            handleCancel={() => setShowModal(false)}
            textConfirm={t('homeScreen.modalToFsc.textConfirm')}
            textCancel={t('homeScreen.modalToFsc.textCancel')}
          />
        </Modal>
      )}
    </>
  );
};

export default MainSideBar;
